/**
 * Risk Buffer Engine — MetalGuard
 *
 * Calcula el buffer de riesgo basado en volatilidad histórica del precio del metal en EUR.
 * Metodología: VaR paramétrico con nivel de confianza del 95% (z = 1.65)
 *
 * Fórmula corregida (captura tanto volatilidad LME USD como EUR/USD):
 *   1. eurPrice_i = LME_USD_i / EURUSD_i  (tipo de cambio histórico del mismo día)
 *   2. daily_return_i = (eurPrice_i − eurPrice_{i−1}) / eurPrice_{i−1}
 *   3. σ_daily = stdDev(daily_returns)  — captura vol LME USD + vol EUR/USD + correlación
 *   4. σ_period = σ_daily × √days
 *   5. adverseMove = σ_period × 1.65  (VaR 95%)
 *   6. risk_buffer = adverseMove × benchmark_EUR_today × yield
 */

import { supabase } from '../lib/supabase';

const Z_95 = 1.65;
const MIN_HISTORY_DAYS = 10;
const DEFAULT_HISTORY_DAYS = 90;

/**
 * Calcula la desviación estándar de un array de números.
 */
function stdDev(values) {
  if (!values || values?.length < 2) return 0;
  const mean = values?.reduce((a, b) => a + b, 0) / values?.length;
  const variance = values?.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (values?.length - 1);
  return Math.sqrt(variance);
}

/**
 * Extrae la fecha calendario (YYYY-MM-DD) de un timestamp ISO.
 */
function toDateKey(ts) {
  return ts ? ts?.slice(0, 10) : null;
}

/**
 * Agrupa ticks por fecha calendario, tomando el último tick del día.
 * @param {Array} ticks - Array de { value, ts, ... }
 * @returns {Object} { 'YYYY-MM-DD': lastTickValue }
 */
function groupTicksByDate(ticks) {
  const map = {};
  if (!ticks) return map;
  for (const tick of ticks) {
    const dateKey = toDateKey(tick?.ts);
    if (!dateKey) continue;
    // Keep the last tick of the day (ticks are ordered ASC, so later overwrites earlier)
    map[dateKey] = parseFloat(tick?.value);
  }
  return map;
}

/**
 * Motor principal ASÍNCRONO del buffer de riesgo.
 * Obtiene ticks LME + EUR/USD históricos de Supabase y los empareja por fecha.
 *
 * @param {string} metalSymbol - Símbolo del metal en market_ticks (p.ej. 'LME_CU')
 * @param {number} daysToSale - Días estimados hasta la venta
 * @param {number} yieldPct - Yield del material (%)
 * @param {number} currentEurUsd - Tipo de cambio EUR/USD actual (para calcular benchmark_EUR_today)
 * @param {number} benchmarkEUR - Precio actual del metal en EUR/t
 * @param {string} userId - ID del usuario en Supabase
 * @param {string} benchmarkType - 'LME' | 'REGIONAL_INDEX' | 'MANUAL'
 * @returns {Promise<Object>} Resultado del cálculo del buffer con componentes sigma
 */
export async function calculateDynamicBuffer({
  metalSymbol,
  daysToSale,
  yieldPct,
  currentEurUsd,
  benchmarkEUR,
  userId,
  benchmarkType = 'LME',
  historyDays = DEFAULT_HISTORY_DAYS,
}) {
  const benchmark = benchmarkEUR || 0;
  const days = Math.max(1, daysToSale || 30);
  const yieldFactor = (yieldPct || 90) / 100;

  const fallback = (reason) => ({
    riskBufferPct: 3,
    riskBufferEurPerTon: 0.03 * benchmark * yieldFactor,
    dailyVolatilityPct: null,
    periodVolatilityPct: null,
    adverseMovePct: 3,
    daysUsed: days,
    historyPointsUsed: 0,
    isFallback: true,
    fallbackReason: reason,
    sigmaLmeUsd: null,
    sigmaEurUsd: null,
    sigmaCombined: null,
  });

  // Non-LME metals: use direct EUR prices (no EUR/USD pairing needed)
  if (benchmarkType !== 'LME') {
    // Fetch LME ticks only
    const since = new Date();
    since?.setDate(since?.getDate() - historyDays);
    const { data: lmeTicks } = await supabase
      ?.from('market_ticks')
      ?.select('value, currency, ts')
      ?.eq('user_id', userId)
      ?.eq('symbol', metalSymbol)
      ?.gte('ts', since?.toISOString())
      ?.order('ts', { ascending: true });

    const prices = (lmeTicks || [])?.map(t => parseFloat(t?.value))?.filter(v => !isNaN(v) && v > 0);
    if (prices?.length < MIN_HISTORY_DAYS) {
      return fallback(`Datos históricos insuficientes para calcular VaR — usando buffer conservador del 3%`);
    }
    const returns = [];
    for (let i = 1; i < prices?.length; i++) {
      if (prices?.[i - 1] > 0) returns?.push((prices?.[i] - prices?.[i - 1]) / prices?.[i - 1]);
    }
    if (returns?.length < MIN_HISTORY_DAYS) {
      return fallback(`Datos históricos insuficientes para calcular VaR — usando buffer conservador del 3%`);
    }
    const sigmaDaily = stdDev(returns);
    const sigmaPeriod = sigmaDaily * Math.sqrt(days);
    const adverseMove = sigmaPeriod * Z_95;
    return {
      riskBufferPct: adverseMove * 100,
      riskBufferEurPerTon: adverseMove * benchmark * yieldFactor,
      dailyVolatilityPct: sigmaDaily * 100,
      periodVolatilityPct: sigmaPeriod * 100,
      adverseMovePct: adverseMove * 100,
      daysUsed: days,
      historyPointsUsed: prices?.length,
      isFallback: false,
      fallbackReason: null,
      sigmaLmeUsd: sigmaDaily * 100,
      sigmaEurUsd: null,
      sigmaCombined: sigmaDaily * 100,
    };
  }

  // LME metal: fetch both LME ticks AND EUR/USD ticks
  const since = new Date();
  since?.setDate(since?.getDate() - historyDays);
  const sinceIso = since?.toISOString();

  const [lmeResult, fxResult] = await Promise.all([
    supabase
      ?.from('market_ticks')
      ?.select('value, currency, ts')
      ?.eq('user_id', userId)
      ?.eq('symbol', metalSymbol)
      ?.gte('ts', sinceIso)
      ?.order('ts', { ascending: true }),
    supabase
      ?.from('market_ticks')
      ?.select('value, ts')
      ?.eq('user_id', userId)
      ?.eq('symbol', 'EUR_USD')
      ?.gte('ts', sinceIso)
      ?.order('ts', { ascending: true }),
  ]);

  // Also try EURUSD symbol as fallback (some seeds use that)
  let fxTicks = fxResult?.data || [];
  if (fxTicks?.length === 0) {
    const { data: fxAlt } = await supabase
      ?.from('market_ticks')
      ?.select('value, ts')
      ?.eq('user_id', userId)
      ?.eq('symbol', 'EURUSD')
      ?.gte('ts', sinceIso)
      ?.order('ts', { ascending: true });
    fxTicks = fxAlt || [];
  }

  const lmeTicks = lmeResult?.data || [];

  if (lmeTicks?.length < MIN_HISTORY_DAYS) {
    return fallback(`Datos históricos insuficientes para calcular VaR — usando buffer conservador del 3%`);
  }

  // Detect whether LME ticks are stored in EUR or USD
  // (seed data stores them in EUR; some live feeds use USD)
  const lmeTickCurrency = lmeTicks?.[0]?.currency || 'USD';
  const lmeInEur = lmeTickCurrency === 'EUR';

  // Group EUR/USD ticks by date (last tick of each day)
  const fxByDate = groupTicksByDate(fxTicks);

  // Pair LME ticks with EUR/USD by date, forward-filling missing EUR/USD values
  const eurPrices = [];
  const lmePricesUsd = [];
  let lastFxRate = currentEurUsd || 1.08; // only used if no historical FX at all

  // Build sorted date list from LME ticks
  const lmeByDate = groupTicksByDate(lmeTicks);
  const lmeDates = Object.keys(lmeByDate)?.sort();

  for (const dateKey of lmeDates) {
    const lmeRaw = lmeByDate?.[dateKey];
    if (!lmeRaw || isNaN(lmeRaw) || lmeRaw <= 0) continue;

    if (lmeInEur) {
      // Ticks already in EUR — use directly, no FX conversion needed
      eurPrices?.push(lmeRaw);
      // Approximate USD value for sigma decomposition display only
      lmePricesUsd?.push(lmeRaw * (currentEurUsd || 1.08));
    } else {
      // Ticks in USD — convert to EUR using historical FX rate
      if (fxByDate?.[dateKey] && fxByDate?.[dateKey] > 0) {
        lastFxRate = fxByDate?.[dateKey];
      }
      const eurPrice = lmeRaw / lastFxRate;
      eurPrices?.push(eurPrice);
      lmePricesUsd?.push(lmeRaw);
    }
  }

  if (eurPrices?.length < MIN_HISTORY_DAYS) {
    return fallback(`Datos históricos insuficientes para calcular VaR — usando buffer conservador del 3%`);
  }

  // Compute daily returns on EUR prices (captures LME USD vol + EUR/USD vol + correlation)
  const combinedReturns = [];
  for (let i = 1; i < eurPrices?.length; i++) {
    if (eurPrices?.[i - 1] > 0) {
      combinedReturns?.push((eurPrices?.[i] - eurPrices?.[i - 1]) / eurPrices?.[i - 1]);
    }
  }

  // Also compute LME-only returns (USD) for sigma decomposition display
  const lmeUsdReturns = [];
  for (let i = 1; i < lmePricesUsd?.length; i++) {
    if (lmePricesUsd?.[i - 1] > 0) {
      lmeUsdReturns?.push((lmePricesUsd?.[i] - lmePricesUsd?.[i - 1]) / lmePricesUsd?.[i - 1]);
    }
  }

  // Compute EUR/USD returns for sigma decomposition display
  const fxDates = Object.keys(fxByDate)?.sort();
  const fxValues = fxDates?.map(d => fxByDate?.[d])?.filter(v => v > 0);
  const fxReturns = [];
  for (let i = 1; i < fxValues?.length; i++) {
    if (fxValues?.[i - 1] > 0) {
      fxReturns?.push((fxValues?.[i] - fxValues?.[i - 1]) / fxValues?.[i - 1]);
    }
  }

  if (combinedReturns?.length < MIN_HISTORY_DAYS) {
    return fallback(`Datos históricos insuficientes para calcular VaR — usando buffer conservador del 3%`);
  }

  const sigmaCombinedDaily = stdDev(combinedReturns);
  const sigmaLmeUsdDaily = stdDev(lmeUsdReturns);
  const sigmaFxDaily = stdDev(fxReturns);

  const sigmaPeriod = sigmaCombinedDaily * Math.sqrt(days);
  const adverseMove = sigmaPeriod * Z_95;
  const riskBufferEurPerTon = adverseMove * benchmark * yieldFactor;

  return {
    riskBufferPct: adverseMove * 100,
    riskBufferEurPerTon,
    dailyVolatilityPct: sigmaCombinedDaily * 100,
    periodVolatilityPct: sigmaPeriod * 100,
    adverseMovePct: adverseMove * 100,
    daysUsed: days,
    historyPointsUsed: eurPrices?.length,
    isFallback: false,
    fallbackReason: null,
    // Sigma decomposition for display
    sigmaLmeUsd: sigmaLmeUsdDaily * 100,
    sigmaEurUsd: sigmaFxDaily * 100,
    sigmaCombined: sigmaCombinedDaily * 100,
  };
}

/**
 * Convierte una serie de ticks de mercado en precios EUR/t.
 * Versión síncrona (usa tipo de cambio fijo — para compatibilidad con código existente).
 */
export function ticksToEurPrices(ticks, eurUsd, benchmarkType) {
  if (!ticks || ticks?.length === 0) return [];
  const rate = eurUsd && eurUsd > 0 ? eurUsd : 1;
  return ticks?.map(t => {
    const val = parseFloat(t?.value);
    if (isNaN(val)) return null;
    if (benchmarkType === 'LME') return val / rate;
    return val;
  })?.filter(v => v !== null && v > 0);
}

/**
 * Calcula los cambios porcentuales diarios de una serie de precios.
 *
 * @param {Array<number>} prices - Serie de precios en orden cronológico
 * @returns {Array<number>} Cambios porcentuales (fracción, no %)
 */
export function dailyReturns(prices) {
  if (!prices || prices?.length < 2) return [];
  const returns = [];
  for (let i = 1; i < prices?.length; i++) {
    if (prices?.[i - 1] > 0) {
      returns?.push((prices?.[i] - prices?.[i - 1]) / prices?.[i - 1]);
    }
  }
  return returns;
}

/**
 * Calcula la volatilidad diaria (desviación estándar de retornos diarios).
 *
 * @param {Array<number>} returns - Array de retornos diarios (fracción)
 * @returns {number} Volatilidad diaria (fracción)
 */
export function dailyVolatility(returns) {
  return stdDev(returns);
}

/**
 * Motor síncrono del buffer de riesgo (mantiene compatibilidad con código existente).
 * Para LME, usa tipo de cambio fijo — para cálculo histórico correcto usar calculateDynamicBuffer.
 */
export function calcDynamicRiskBuffer({
  ticks,
  eurUsd,
  benchmarkType,
  benchmarkEUR,
  daysToSale,
  yieldPct,
  historyDays = 60,
}) {
  const benchmark = benchmarkEUR || 0;
  const days = Math.max(1, daysToSale || 30);
  const yieldFactor = (yieldPct || 90) / 100;

  if (!ticks || ticks?.length < MIN_HISTORY_DAYS) {
    const fallbackPct = 0.03;
    return {
      riskBufferPct: fallbackPct * 100,
      riskBufferEurPerTon: fallbackPct * benchmark * yieldFactor,
      dailyVolatilityPct: null,
      periodVolatilityPct: null,
      adverseMovePct: fallbackPct * 100,
      daysUsed: days,
      historyPointsUsed: ticks?.length || 0,
      isFallback: true,
      fallbackReason: `Datos históricos insuficientes (${ticks?.length || 0} puntos, mínimo ${MIN_HISTORY_DAYS})`,
      sigmaLmeUsd: null,
      sigmaEurUsd: null,
      sigmaCombined: null,
    };
  }

  const eurPrices = ticksToEurPrices(ticks, eurUsd, benchmarkType);

  if (eurPrices?.length < MIN_HISTORY_DAYS) {
    const fallbackPct = 0.03;
    return {
      riskBufferPct: fallbackPct * 100,
      riskBufferEurPerTon: fallbackPct * benchmark * yieldFactor,
      dailyVolatilityPct: null,
      periodVolatilityPct: null,
      adverseMovePct: fallbackPct * 100,
      daysUsed: days,
      historyPointsUsed: eurPrices?.length,
      isFallback: true,
      fallbackReason: `Precios EUR insuficientes (${eurPrices?.length} puntos válidos)`,
      sigmaLmeUsd: null,
      sigmaEurUsd: null,
      sigmaCombined: null,
    };
  }

  const returns = dailyReturns(eurPrices);

  if (returns?.length < 2) {
    const fallbackPct = 0.03;
    return {
      riskBufferPct: fallbackPct * 100,
      riskBufferEurPerTon: fallbackPct * benchmark * yieldFactor,
      dailyVolatilityPct: null,
      periodVolatilityPct: null,
      adverseMovePct: fallbackPct * 100,
      daysUsed: days,
      historyPointsUsed: eurPrices?.length,
      isFallback: true,
      fallbackReason: 'Retornos insuficientes para calcular volatilidad',
      sigmaLmeUsd: null,
      sigmaEurUsd: null,
      sigmaCombined: null,
    };
  }

  const sigmaDaily = dailyVolatility(returns);
  const sigmaPeriod = sigmaDaily * Math.sqrt(days);
  const adverseMove = sigmaPeriod * Z_95;
  const riskBufferEurPerTon = adverseMove * benchmark * yieldFactor;

  return {
    riskBufferPct: adverseMove * 100,
    riskBufferEurPerTon,
    dailyVolatilityPct: sigmaDaily * 100,
    periodVolatilityPct: sigmaPeriod * 100,
    adverseMovePct: adverseMove * 100,
    daysUsed: days,
    historyPointsUsed: eurPrices?.length,
    isFallback: false,
    fallbackReason: null,
    sigmaLmeUsd: sigmaDaily * 100,
    sigmaEurUsd: null,
    sigmaCombined: sigmaDaily * 100,
  };
}

/**
 * Calcula el buffer de riesgo en modo manual (porcentaje fijo del usuario).
 *
 * @param {number} manualPct - Porcentaje manual introducido por el usuario
 * @param {number} benchmarkEUR - Precio actual del metal en EUR/t
 * @param {number} yieldPct - Yield del material (%)
 * @returns {Object} Resultado del buffer manual
 */
export function calcManualRiskBuffer(manualPct, benchmarkEUR, yieldPct) {
  const pct = manualPct || 0;
  const benchmark = benchmarkEUR || 0;
  const yieldFactor = (yieldPct || 90) / 100;
  return {
    riskBufferPct: pct,
    riskBufferEurPerTon: (pct / 100) * benchmark * yieldFactor,
    dailyVolatilityPct: null,
    periodVolatilityPct: null,
    adverseMovePct: pct,
    daysUsed: null,
    historyPointsUsed: null,
    isFallback: false,
    isManual: true,
    fallbackReason: null,
    sigmaLmeUsd: null,
    sigmaEurUsd: null,
    sigmaCombined: null,
  };
}
